import React, { useState, useEffect, useCallback } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import './BulkAssetEditor.css';
import './AssetCodeTool.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import SpreadsheetTable from './SpreadsheetTable';
import {
  FaArrowLeft, FaBuilding, FaSearch, FaUserCircle, FaSignOutAlt, FaBell,
  FaServer, FaTable, FaFilter, FaDownload, FaChevronLeft, FaChevronRight, FaBars
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const BulkAssetEditor = () => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userName, setUserName] = useState('');

  // Filter state
  const [buildings, setBuildings] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedAssetType, setSelectedAssetType] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [refFilter, setRefFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Data state
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageInfo, setPageInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasLoaded, setHasLoaded] = useState(false);

  // All asset types flattened
  const allAssetTypes = modules.flatMap(m => m.asset_types.map(at => ({ ...at, module_key: m.key, module_label: m.label })));

  const columns = [
    { key: 'id', label: 'ID', editable: false, width: '70px' },
    { key: 'name', label: 'Name', editable: false, width: '200px' },
    { key: 'building_name', label: 'Building', editable: false, width: '180px' },
    { key: 'reference_id', label: 'Reference Number', editable: true, width: '200px' },
    { key: 'type', label: 'Asset Type', editable: true, type: 'dropdown', options: [], width: '160px' },
    { key: 'location_description', label: 'Location', editable: true, width: '200px' },
    { key: 'nfc_tag_id', label: 'NFC ID', editable: true, width: '150px' },
    { key: 'status', label: 'Status', editable: false, width: '100px' },
  ];

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') {
      toast.error('Access denied'); navigate('/login'); return;
    }
    loadInitialData();
  }, [navigate]);

  const loadInitialData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const [bldgRes, modRes, profRes] = await Promise.all([
        fetch(getApiEndpoint('/api/cloud-staff/buildings'), { headers }),
        fetch(getApiEndpoint('/api/cloud-staff/modules'), { headers }),
        fetch(getApiEndpoint('/api/users/profile'), { headers })
      ]);
      if (bldgRes.ok) setBuildings(await bldgRes.json());
      if (modRes.ok) setModules(await modRes.json());
      if (profRes.ok) {
        const d = await profRes.json();
        setUserName(`${d.firstName || d.first_name || ''} ${d.lastName || d.last_name || ''}`);
      }
    } catch (e) {
      console.error('Error loading data:', e);
      toast.error('Failed to load initial data');
    }
  };

  const loadAssets = useCallback(async (page = 1) => {
    if (!selectedAssetType) {
      toast.warning('Please select an asset type');
      return;
    }

    setLoading(true);
    setHasLoaded(true);

    try {
      const params = new URLSearchParams({
        asset_type_key: selectedAssetType,
        page: String(page),
        per_page: '100'
      });
      if (selectedBuilding) params.set('property_id', selectedBuilding);
      if (statusFilter) params.set('status', statusFilter);
      if (refFilter) params.set('ref_filter', refFilter);
      if (searchTerm) params.set('search', searchTerm);

      const res = await fetch(getApiEndpoint(`/api/cloud-staff/bulk-editor/assets?${params}`), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (res.ok) {
        const data = await res.json();
        setAssets(data.data || []);
        setPageInfo(data.pagination || null);
        setCurrentPage(data.pagination?.page || 1);
      } else {
        toast.error('Failed to load assets');
      }
    } catch (e) {
      console.error('Load error:', e);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [selectedAssetType, selectedBuilding, statusFilter, refFilter, searchTerm]);

  const handleSave = async (changes) => {
    const res = await fetch(getApiEndpoint('/api/cloud-staff/bulk-editor/save'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ asset_type_key: selectedAssetType, changes })
    });

    if (res.ok) {
      const result = await res.json();
      toast.success(`Saved ${result.total_updated} change(s)`);
      if (result.total_errors > 0) toast.warning(`${result.total_errors} error(s)`);
      loadAssets(currentPage);
    } else {
      const err = await res.json();
      toast.error(err.message || 'Save failed');
      throw new Error(err.message);
    }
  };

  const handleValidate = async (changes) => {
    const res = await fetch(getApiEndpoint('/api/cloud-staff/bulk-editor/validate'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ asset_type_key: selectedAssetType, changes })
    });
    if (res.ok) return await res.json();
    return { valid: false, errors: [{ message: 'Validation request failed' }] };
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge"><FaServer /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn"><FaBell /></button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header">
                    <FaUserCircle className="cs-menu-avatar" />
                    <div>
                      <p className="cs-menu-name">{userName || 'Cloud Staff'}</p>
                      <p className="cs-menu-role">Cloud Staff Admin</p>
                    </div>
                  </div>
                  <div className="cs-user-menu-divider" />
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout"><FaSignOutAlt /> Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar active="bulk-editor" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />

        <main className="cs-content" style={{ maxWidth: '1600px' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #21262d' }}>
            <Link to="/cloud-staff/dashboard" className="act-back-btn"><FaArrowLeft /></Link>
            <div>
              <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f0f6fc', margin: 0 }}>
                <FaTable style={{ marginRight: '0.5rem' }} /> Bulk Asset Editor
              </h1>
              <p style={{ fontSize: '0.8rem', color: '#8b949e', margin: '0.2rem 0 0' }}>
                Spreadsheet-style editing — Click cells to edit, Tab/Enter to navigate, Ctrl+Z undo, Ctrl+S save, paste from Excel
              </p>
            </div>
          </div>

          {/* Filter Panel */}
          <div className="be-filter-panel">
            <div className="be-filter-row">
              <div className="be-filter-group">
                <label>Building</label>
                <select value={selectedBuilding} onChange={(e) => setSelectedBuilding(e.target.value)}>
                  <option value="">All Buildings</option>
                  {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>

              <div className="be-filter-group">
                <label>Asset Type *</label>
                <select value={selectedAssetType} onChange={(e) => setSelectedAssetType(e.target.value)}>
                  <option value="">Select asset type...</option>
                  {allAssetTypes.map(at => (
                    <option key={at.key} value={at.key}>{at.module_label} → {at.label}</option>
                  ))}
                </select>
              </div>

              <div className="be-filter-group">
                <label>Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All</option>
                  <option value="Pass">Pass</option>
                  <option value="Fail">Fail</option>
                  <option value="Pending">Pending</option>
                  <option value="Unknown">Unknown</option>
                </select>
              </div>

              <div className="be-filter-group">
                <label>Reference</label>
                <select value={refFilter} onChange={(e) => setRefFilter(e.target.value)}>
                  <option value="">All</option>
                  <option value="has_ref">Has Reference</option>
                  <option value="missing_ref">Missing Reference</option>
                </select>
              </div>

              <div className="be-filter-group">
                <label>Search</label>
                <div className="be-search-input">
                  <FaSearch />
                  <input type="text" placeholder="Search by ID or name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="be-filter-actions">
              <button className="be-load-btn" onClick={() => loadAssets(1)} disabled={!selectedAssetType || loading}>
                {loading ? 'Loading...' : <><FaFilter /> Load Assets</>}
              </button>
            </div>
          </div>

          {/* Spreadsheet Table */}
          {hasLoaded && (
            <>
              <SpreadsheetTable
                columns={columns}
                data={assets}
                onSave={handleSave}
                onValidate={handleValidate}
                idField="id"
                loading={loading}
                pageInfo={pageInfo}
              />

              {/* Pagination */}
              {pageInfo && pageInfo.total_pages > 1 && (
                <div className="be-pagination">
                  <button onClick={() => loadAssets(currentPage - 1)} disabled={currentPage <= 1} className="be-page-btn">
                    <FaChevronLeft /> Previous
                  </button>
                  <span className="be-page-info">Page {currentPage} of {pageInfo.total_pages} ({pageInfo.total} total)</span>
                  <button onClick={() => loadAssets(currentPage + 1)} disabled={currentPage >= pageInfo.total_pages} className="be-page-btn">
                    Next <FaChevronRight />
                  </button>
                </div>
              )}
            </>
          )}

          {!hasLoaded && (
            <div className="act-prompt">
              <div className="act-prompt-icon"><FaTable /></div>
              <h2>Configure Filters & Load Assets</h2>
              <p>Select an asset type above, then click "Load Assets" to open the spreadsheet editor. You can filter by building, status, and reference to narrow down results.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default BulkAssetEditor;
